import {
    ChangePasswordRequest,
    Configuration,
    CreatePasswordWithTotpRequest,
    CreateTotpRequest,
    DefaultApi,
    RequiredError,
    ResponseError,
    SetPasswordRequest,
} from "src/api/generated/frontend_user_v1";
import { ERROR_STORE } from "src/context/error-context";
import Console from "src/utils/console";
import { UUID } from "src/api/api_public";
import { CreateWebauthnRequest } from "src/api/generated/frontend_public_v1";

const configuration = new Configuration({
    basePath: window.location.origin,
});

const defaultApi = new DefaultApi(configuration);

export const ApiUser = {
    auth: {
        getMe: () => defaultApi.getMyUser(),
        authenticateTailscaleDevice: (token: UUID) =>
            defaultApi.authenticateTailscaleDevice({ tailscale_token: token }),
    },
    credentials: {
        get: () => handleError(defaultApi.getCredentials()),
        createPassword: (request: SetPasswordRequest) =>
            handleError(defaultApi.createNewPassword({ SetPasswordRequest: request })),
        createPasswordWithTotp: (request: CreatePasswordWithTotpRequest) =>
            handleError(defaultApi.createPasswordWithTotp({ CreatePasswordWithTotpRequest: request })),
        changePassword: (request: ChangePasswordRequest) =>
            handleError(defaultApi.changePassword({ ChangePasswordRequest: request })),
        deleteWebauthnKey: (webauthnKeyUuid: UUID) => handleError(defaultApi.deleteWebauthn({ uuid: webauthnKeyUuid })),
        deletePassword: () => handleError(defaultApi.deletePassword()),
        createTotp: (request: CreateTotpRequest) => handleError(defaultApi.createTotp({ CreateTotpRequest: request })),
        deleteTotp: (uuid: UUID) => handleError(defaultApi.deleteTotp({ uuid })),
        createWebauthnKey: (request: CreateWebauthnRequest) =>
            handleError(defaultApi.createWebauthn({ CreateWebauthnRequest: request })),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        completeWebauthn: (body: any) => handleError(defaultApi.completeWebauthn({ body })),
    },
    localUser: {
        getAccessExpiration: () => handleError(defaultApi.getUserAccessExpiration()),
    },
};

/**
 * Wraps a promise returned by the generated SDK which handles its errors and returns a {@link Result}
 *
 * @param promise The promise to wrap. This should be a promise defined in the generated part of the API
 *
 * @returns a new promise with a result that wraps errors from the API
 */
export async function handleError<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise;
    } catch (e) {
        let msg;
        if (e instanceof ResponseError) {
            msg = e.response.statusText;
        } else if (e instanceof RequiredError) {
            Console.error(e);
            msg = "The server's response didn't match the spec";
        } else {
            Console.error("Unknown error occurred:", e);
            msg = "Unknown error occurred";
        }
        ERROR_STORE.report(msg);
        throw msg;
    }
}
